import { useState, useEffect } from "react";
import { Link } from "@tanstack/react-router";
import {
  Plus,
  Edit,
  Trash2,
  Power,
  Calendar,
  MapPin,
  Eye,
  Users,
} from "lucide-react";
import OrganizationEventForm from "./OrganizationEventForm";
import DeleteConfirmDialog from "./DeleteConfirmDialog";
import { V1BEClient } from "@/clients";
import {
  IOrganizationEvent,
  ICreateOrganizationEventPayload,
} from "@/types/EventTypes";
import {
  countGuestsOverExpected,
  countSuppliersOverExpected,
} from "@/lib/utils";

const OrganizationEvents = () => {
  const [events, setEvents] = useState<IOrganizationEvent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState<IOrganizationEvent | null>(
    null,
  );
  const [deleteEvent, setDeleteEvent] = useState<IOrganizationEvent | null>(
    null,
  );
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Load organization events from API
  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    setIsLoading(true);
    try {
      const eventsData = await V1BEClient.getOrganizationEvents();
      setEvents(eventsData);
    } catch (error) {
      console.error("Error loading organization events:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateEvent = () => {
    setEditingEvent(null);
    setShowForm(true);
  };

  const handleEditEvent = (event: IOrganizationEvent) => {
    setEditingEvent(event);
    setShowForm(true);
  };

  const handleDeleteEvent = (event: IOrganizationEvent) => {
    setDeleteEvent(event);
    setShowDeleteDialog(true);
  };

  const handleSaveEvent = async (
    eventData: ICreateOrganizationEventPayload,
  ) => {
    try {
      let savedEventId: string | undefined;

      if (editingEvent) {
        // Update existing event
        const success = await V1BEClient.updateOrganizationEvent(eventData);
        if (success) {
          savedEventId = eventData.id;
        }
      } else {
        // Create new event
        const newEvent = await V1BEClient.createOrganizationEvent(eventData);
        if (newEvent) {
          savedEventId = newEvent.id;
        }
      }

      // Save string properties if event was saved/created successfully
      if (
        savedEventId &&
        eventData.organization_event_string_properties &&
        eventData.organization_event_string_properties.length > 0
      ) {
        await V1BEClient.createOrganizationEventStringProperties(
          savedEventId,
          eventData.organization_event_string_properties,
        );
      }

      // Save int properties if event was saved/created successfully
      if (
        savedEventId &&
        eventData.organization_event_int_properties &&
        eventData.organization_event_int_properties.length > 0
      ) {
        await V1BEClient.createOrganizationEventIntProperties(
          savedEventId,
          eventData.organization_event_int_properties,
        );
      }

      if (savedEventId) {
        await loadEvents(); // Reload events from API
        setShowForm(false);
        setEditingEvent(null);
      }
    } catch (error) {
      console.error("Error saving event:", error);
    }
  };

  const handleConfirmDelete = async () => {
    if (deleteEvent) {
      try {
        // Delete image if exists
        if (deleteEvent.image_url) {
          await V1BEClient.deleteFile(deleteEvent.image_url);
        }

        const success = await V1BEClient.deleteOrganizationEvent(
          deleteEvent.id,
        );
        if (success) {
          await loadEvents(); // Reload events from API
        }
      } catch (error) {
        console.error("Error deleting event:", error);
      }
      setDeleteEvent(null);
    }
    setShowDeleteDialog(false);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatDateRange = (dateFrom?: string, dateTo?: string) => {
    if (!dateFrom) return "No date set";

    const from = new Date(dateFrom);
    if (!dateTo) return formatDate(dateFrom);

    const to = new Date(dateTo);

    if (from.toDateString() === to.toDateString()) {
      return formatDate(dateFrom);
    }

    return `${formatDate(dateFrom)} - ${formatDate(dateTo)}`;
  };

  const truncateDescription = (htmlContent: string, maxLength: number = 25) => {
    if (!htmlContent) return "No description provided";

    // Strip HTML tags
    const textContent = htmlContent.replace(/<[^>]*>/g, "");

    // Truncate to maxLength characters
    if (textContent.length <= maxLength) {
      return textContent;
    }

    return textContent.substring(0, maxLength) + "...";
  };

  const getAbbreviation = (name: string) => {
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <div className="p-8">
      <div className="flex md:flex-row flex-col justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Organization Events
          </h1>
          <p className="text-gray-600">
            Manage your organization's events and activities
          </p>
        </div>
        <button
          onClick={handleCreateEvent}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors duration-200 flex items-center gap-2 ml-auto"
        >
          <Plus size={20} />
          Create Event
        </button>
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading events...</p>
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <Calendar size={64} className="mx-auto" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            No events created
          </h3>
          <p className="text-gray-600 mb-6">
            Get started by creating your first organization event.
          </p>
          <button
            onClick={handleCreateEvent}
            className="bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors duration-200 flex items-center gap-2 mx-auto"
          >
            <Plus size={20} />
            Create Your First Event
          </button>
        </div>
      ) : (
        <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
          {events.map((event) => (
            <div
              key={event.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-300 break-inside-avoid mb-6"
            >
              {event.image_url ? (
                <div
                  onClick={() => handleEditEvent(event)}
                  className="w-full relative cursor-pointer"
                >
                  <img
                    src={event.image_url}
                    alt={event.name}
                    className="w-full h-auto object-cover"
                  />
                </div>
              ) : (
                <div className="h-48 w-full overflow-hidden bg-primary-100 flex items-center justify-center">
                  <span className="text-4xl font-bold text-primary-600">
                    {getAbbreviation(event.name)}
                  </span>
                </div>
              )}
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                      {event.name}
                    </h3>
                    <div className="flex flex-wrap gap-2 mb-2">
                      <span className="inline-flex items-center text-xs text-gray-500 bg-gray-100 rounded-full px-3 py-1">
                        <Users size={14} className="mr-1" />
                        {countSuppliersOverExpected(
                          event.organization_event_attendees || [],
                          event.organization_event_int_properties || [],
                        )}
                      </span>
                      <span className="inline-flex items-center text-xs text-gray-500 bg-gray-100 rounded-full px-3 py-1">
                        <Users size={14} className="mr-1" />
                        {countGuestsOverExpected(
                          event.organization_event_attendees || [],
                          event.organization_event_int_properties || [],
                        )}
                      </span>
                    </div>
                    <p className="text-gray-600 text-sm line-clamp-3 mb-4">
                      {truncateDescription(event.description || "")}
                    </p>
                  </div>
                </div>

                <div className="space-y-2 mb-6">
                  <div className="flex items-center text-gray-500 text-sm">
                    <Calendar size={16} className="mr-2" />
                    <span>
                      {formatDateRange(event.date_from, event.date_to)}
                    </span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Link
                    to="/events/$eventId"
                    params={{ eventId: event.id.toString() }}
                    className="flex-1 bg-primary-100 text-primary-700 px-3 py-2 rounded-lg hover:bg-primary-200 transition-colors duration-200 flex items-center justify-center gap-2 text-sm"
                  >
                    <Eye size={16} />
                    Details
                  </Link>
                  <button
                    onClick={() => handleEditEvent(event)}
                    className="flex-1 bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors duration-200 flex items-center justify-center gap-2 text-sm"
                  >
                    <Edit size={16} />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteEvent(event)}
                    className="bg-red-100 text-red-700 px-3 py-2 rounded-lg hover:bg-red-200 transition-colors duration-200 flex items-center justify-center"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Event Form Dialog */}
      {showForm && (
        <OrganizationEventForm
          event={editingEvent}
          onSave={handleSaveEvent}
          onClose={() => {
            setShowForm(false);
            setEditingEvent(null);
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && deleteEvent && (
        <DeleteConfirmDialog
          event={deleteEvent}
          onConfirm={handleConfirmDelete}
          onCancel={() => {
            setShowDeleteDialog(false);
            setDeleteEvent(null);
          }}
        />
      )}
    </div>
  );
};

export default OrganizationEvents;
